import { NonEmptyString } from "@heliopolis/schema/non-empty-string.js";
import { asError } from "@heliopolis/utils/as-error.js";
import { ResultAsync } from "neverthrow";
import fs from "node:fs/promises";
import path from "node:path";
import * as z from "zod";

export const TaskDocumentIdentifier = NonEmptyString;
export type TaskDocumentIdentifier = z.output<typeof TaskDocumentIdentifier>;

export const TaskDocumentEntry = z.object({
  id: TaskDocumentIdentifier,
  title: NonEmptyString,
});
export type TaskDocumentEntry = z.output<typeof TaskDocumentEntry>;

export const TaskDocument = z.object({
  id: TaskDocumentIdentifier,
  markdown: z.string(),
  metadata: z.object({ title: NonEmptyString }),
});
export type TaskDocument = z.output<typeof TaskDocument>;

function taskDocumentDirectory(directory?: string): string {
  return directory ?? path.resolve("tasks");
}

export function listTaskDocuments(
  directory?: string,
): ResultAsync<Array<TaskDocumentEntry>, Error> {
  const taskDirectory = taskDocumentDirectory(directory);

  return ResultAsync.fromPromise(
    (async function readTaskDocuments(): Promise<Array<TaskDocumentEntry>> {
      const directoryEntries = await fs.readdir(taskDirectory, {
        recursive: true,
        withFileTypes: true,
      });

      const taskDocumentEntries = await Promise.all(
        directoryEntries.map(async function taskDocumentEntry(entry) {
          if (!entry.isFile() || path.extname(entry.name) !== ".md") return undefined;

          const filePath = path.join(entry.parentPath, entry.name);
          try {
            await fs.access(filePath, fs.constants.R_OK);
          } catch {
            return undefined;
          }

          return TaskDocumentEntry.decode({
            id: path.relative(taskDirectory, filePath).split(path.sep).join("/"),
            title: path.basename(entry.name, ".md"),
          });
        }),
      );

      return taskDocumentEntries
        .filter((entry): entry is TaskDocumentEntry => entry !== undefined)
        .toSorted((left, right) => left.id.localeCompare(right.id));
    })(),
    asError,
  );
}

export function findTaskDocument(id: string, directory?: string): ResultAsync<TaskDocument, Error> {
  return ResultAsync.fromPromise(
    (async () => {
      const identifier = TaskDocumentIdentifier.safeDecode(id);
      if (!identifier.success) throw new Error("A task document identifier is required");

      if (path.isAbsolute(identifier.data) || identifier.data.includes("\\"))
        throw new Error(`No task document matches "${identifier.data}"`);

      const taskDirectory = taskDocumentDirectory(directory);
      const filePath = path.resolve(taskDirectory, identifier.data);
      const relativePath = path.relative(taskDirectory, filePath);
      if (
        relativePath === ".." ||
        relativePath.startsWith(`..${path.sep}`) ||
        path.isAbsolute(relativePath) ||
        path.extname(relativePath) !== ".md"
      )
        throw new Error(`No task document matches "${identifier.data}"`);

      const taskDirectoryStat = await fs.stat(taskDirectory);
      if (!taskDirectoryStat.isDirectory())
        throw new Error(`Task document directory "${taskDirectory}" is invalid`);

      let file: Awaited<ReturnType<typeof fs.lstat>>;
      try {
        file = await fs.lstat(filePath);
      } catch {
        throw new Error(`No task document matches "${identifier.data}"`);
      }

      if (!file.isFile()) throw new Error(`No task document matches "${identifier.data}"`);

      return TaskDocument.decode({
        id: relativePath.split(path.sep).join("/"),
        markdown: await fs.readFile(filePath, "utf-8"),
        metadata: { title: path.basename(filePath, ".md") },
      });
    })(),
    asError,
  );
}
